import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createForm } from "@formily/core";
import {
  ArrowLeft,
  Search,
  Download,
  Upload,
  UserPlus,
  Briefcase,
  Mail,
  Phone,
  Trash2,
  Users,
  Pencil,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { AdminLayout } from "@sd/components/admin/AdminLayout";
import { useCompaniesAdmin } from "@sd/context/CompaniesAdminContext";
import { sd } from "@sd/lib/sdPaths";
import type { CompanyMember, CustomerCompany } from "@sd/types/customerCompany";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import { notify } from "@/components/common/Notification";
import { normalizeDynamicModuleUploadFields } from "@/lib/normalizeDynamicModuleUploadFields";
import { removeMetaFields } from "@/lib/removeMetaFields";
import ServiceDeskAPIService from "@sd/services/APIs";
import { extractListArray } from "@sd/lib/formilyGridTicketCache";
import {
  SERVICE_DESK_FORMILY_ADD_MEMBER_MODEL,
  SERVICE_DESK_ADD_MEMBER_COMPANY_FK_FIELD,
  serviceDeskFormilyAddMemberGridQueryKey,
  ServiceDeskAPI,
  serviceDeskEmployeeListByCompanyQueryKey,
} from "@sd/services/APIs";
import { SchemaFormDialog } from "@sd/components/common/SchemaFormDialog";

function pickStr(raw: Record<string, unknown>, keys: string[]): string {
  for (const k of keys) {
    const v = raw[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") {
      return String(v);
    }
  }
  return "";
}

function isLikelyUuid(value: unknown): boolean {
  if (typeof value !== "string") {
    return false;
  }
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    value.trim()
  );
}

/** Like pickStr but skips UUID-looking values (use after trying `*_name` keys). */
function pickStrSkipUuid(raw: Record<string, unknown>, keys: string[]): string {
  for (const k of keys) {
    const v = raw[k];
    if (v === undefined || v === null) {
      continue;
    }
    const s = String(v).trim();
    if (s !== "" && !isLikelyUuid(s)) {
      return s;
    }
  }
  return "";
}

/** Parent company id on a member row (Formily FK or employee API). */
function parentCompanyIdFromRaw(raw: Record<string, unknown>): string | null {
  // Prefer `company_id` when present — grid often fills this; avoids stale relation keys.
  const keys = [
    "company_id",
    SERVICE_DESK_ADD_MEMBER_COMPANY_FK_FIELD,
    "customer_company_id",
    "customer_company",
    "company",
    "service_desk_customer_company",
  ];
  for (const k of keys) {
    if (!(k in raw)) {
      continue;
    }
    const v = raw[k];
    if (v === undefined || v === null) {
      continue;
    }
    if (typeof v === "object" && !Array.isArray(v) && "id" in v) {
      const id = String((v as { id: unknown }).id ?? "").trim();
      if (id !== "") {
        return id;
      }
      continue;
    }
    const s = String(v).trim();
    if (s !== "") {
      return s;
    }
  }
  return null;
}

type MemberRowFilterMode = "lenient" | "strict";

/**
 * Drop rows that belong to another company when the payload is over-broad.
 * - lenient (employee list): if no row has a parent id, keep all (API-only filter).
 * - strict (Formily grid): keep only rows where parent id matches; drop orphans / other companies.
 */
function filterMemberRawRowsByCompany<T extends Record<string, unknown>>(
  rows: T[],
  companyId: string | undefined,
  mode: MemberRowFilterMode = "lenient"
): T[] {
  if (!companyId || rows.length === 0) {
    return rows;
  }
  const parents = rows.map(r => parentCompanyIdFromRaw(r));

  if (mode === "strict") {
    return rows.filter((_, i) => parents[i] === companyId);
  }

  const anyScoped = parents.some(p => p !== null);
  if (!anyScoped) {
    return rows;
  }
  return rows.filter((_, i) => parents[i] === companyId);
}

/** Unified list row: Formily records are editable; employee_list rows are read-only in UI. */
type MemberListRow = CompanyMember & {
  listRowSource: "employee" | "formily";
  /** Extra line for employee API rows (timezone, company_id, …). */
  employeeMetaLine?: string;
};

/** Try several param values — API often keys off domain slug (e.g. nnnnn) not display name. */
function employeeListCompanyNameCandidates(c: CustomerCompany): string[] {
  const out: string[] = [];
  const push = (s: string) => {
    const t = s.trim();
    if (t && !out.includes(t)) {
      out.push(t);
    }
  };
  const dom = c.domain.replace(/^@/, "").trim();
  let domainToken = "";
  if (dom) {
    if (dom.includes("@")) {
      domainToken = dom.split("@")[0]?.trim() ?? "";
    } else {
      domainToken = dom.split(".")[0]?.trim() ?? "";
    }
  }
  const rawName = c.name.trim();
  if (domainToken) {
    push(domainToken);
  }
  if (rawName) {
    push(rawName);
  }
  if (dom && !dom.includes("@")) {
    push(dom);
  }
  for (const x of [...out]) {
    const low = x.toLowerCase();
    if (low !== x) {
      push(low);
    }
  }
  return out.filter(Boolean);
}

/**
 * Employee list responses often use `employees`, nested `data`, or odd envelopes —
 * go beyond generic extractListArray when needed.
 */
function extractEmployeeListRows(payload: unknown): unknown[] {
  const direct = extractListArray(payload);
  if (direct.length > 0) {
    return direct;
  }
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return [];
  }
  const o = payload as Record<string, unknown>;
  const extraKeys = [
    "employees",
    "employee_list",
    "employee_data",
    "users",
    "user_list",
  ];
  for (const k of extraKeys) {
    const v = o[k];
    if (Array.isArray(v) && v.length > 0) {
      return v;
    }
  }
  const single = o.employee;
  if (single && typeof single === "object" && !Array.isArray(single)) {
    return [single];
  }
  for (const nest of ["data", "payload", "result", "response"]) {
    const inner = o[nest];
    if (inner && typeof inner === "object") {
      const found = extractEmployeeListRows(inner);
      if (found.length > 0) {
        return found;
      }
    }
  }
  return [];
}

function fullNameFromParts(raw: Record<string, unknown>): string {
  const first = pickStr(raw, ["first_name", "firstname"]);
  const last = pickStr(raw, ["last_name", "lastname"]);
  if (first && last) {
    return `${first} ${last}`;
  }
  return first || last || "";
}

/** Job title / designation / Service Desk role — prefer `*_name` fields over UUIDs. */
function employeeRoleLineFromFlat(flat: Record<string, unknown>): string {
  const job = pickStr(flat, ["jobtitle", "job_title", "title"]);
  const des =
    pickStr(flat, ["designation_name"]) ||
    pickStrSkipUuid(flat, ["designation", "designations"]);
  const sdr =
    pickStr(flat, ["servicedesk_role_name", "service_desk_role_name"]) ||
    pickStrSkipUuid(flat, ["servicedesk_role", "service_desk_role"]);
  const parts = [job, des, sdr].filter(Boolean);
  if (parts.length > 0) {
    return parts.join(" · ");
  }
  return (
    pickStr(flat, [
      "role",
      "department",
      "job_role",
      "emp_role",
      "employee_name",
    ]) || "Member"
  );
}

function employeeMetaLineFromFlat(
  flat: Record<string, unknown>
): string | undefined {
  const parts: string[] = [];
  const tz = pickStr(flat, ["timezone"]);
  if (tz) {
    parts.push(tz);
  }
  const cid = flat.company_id;
  if (cid !== undefined && cid !== null && String(cid).trim() !== "") {
    parts.push(`Company ID ${String(cid)}`);
  }
  if (flat.is_active === false) {
    parts.push("Inactive");
  }
  if (parts.length === 0) {
    return undefined;
  }
  return parts.join(" · ");
}

/** Map Formily member grid row → list row (field names vary by schema). */
function mapFormilyRowToMember(raw: Record<string, unknown>): MemberListRow {
  const id =
    String(raw.id ?? raw.pk ?? "").trim() ||
    `row-${pickStr(raw, ["email", "fullname", "name"])}`;
  return {
    id,
    name:
      pickStr(raw, [
        "fullname",
        "full_name",
        "name",
        "member_name",
        "username",
        "contact_name",
      ]) || "—",
    email: pickStr(raw, ["email", "email_id", "mail", "work_email"]) || "—",
    phone: pickStr(raw, ["phone", "mobile", "contact_phone", "phone_number"]) || "—",
    role:
      pickStr(raw, [
        "jobtitle",
        "job_title",
        "title",
        "role",
        "designation",
      ]) || "Member",
    isPrimary: Boolean(raw.is_primary ?? raw.isPrimary),
    listRowSource: "formily",
  };
}

/** Some employee APIs nest profile under `user` / `employee`. */
function flattenEmployeeRaw(
  raw: Record<string, unknown>
): Record<string, unknown> {
  const merged = { ...raw };
  for (const nestKey of ["user", "employee", "profile", "employee_detail"]) {
    const inner = raw[nestKey];
    if (inner && typeof inner === "object" && !Array.isArray(inner)) {
      Object.assign(merged, inner as Record<string, unknown>);
    }
  }
  return merged;
}

/** Map GET employee_list row → list row (field names vary by API). */
function mapEmployeeRowToMember(raw: Record<string, unknown>): MemberListRow {
  const flat = flattenEmployeeRaw(raw);
  const id =
    String(
      flat.id ??
        flat.pk ??
        flat.employee_id ??
        flat.emp_id ??
        flat.user_id ??
        ""
    ).trim() ||
    `emp-${pickStr(flat, ["emailaddress", "email", "work_email", "mail"])}`;
  const nameParts = fullNameFromParts(flat);
  const name =
    pickStr(flat, [
      "fullname",
      "full_name",
      "name",
      "employee_name",
      "user_name",
      "username",
      "display_name",
    ]) ||
    nameParts ||
    "—";
  const employeeMetaLine = employeeMetaLineFromFlat(flat);
  return {
    id,
    name,
    email:
      pickStr(flat, [
        "emailaddress",
        "email",
        "work_email",
        "email_id",
        "user_email",
        "mail",
        "official_email",
      ]) || "—",
    phone:
      pickStr(flat, [
        "phone",
        "mobile",
        "contact_no",
        "phone_number",
        "mobile_no",
        "contact_number",
      ]) || "—",
    role: employeeRoleLineFromFlat(flat),
    isPrimary: Boolean(flat.is_primary ?? flat.isPrimary),
    listRowSource: "employee",
    ...(employeeMetaLine ? { employeeMetaLine } : {}),
  };
}

/** CSV helpers — kept for template download only; bulk import is not wired to Formily API. */
const CSV_HEADER = "name,email,phone,role";

export function AdminCompanyMembersPage() {
  return (
    <AdminLayout>
      <AdminCompanyMembers />
    </AdminLayout>
  );
}

function AdminCompanyMembers() {
  const { companyId } = useParams<{ companyId: string }>();
  const importInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const { companies } = useCompaniesAdmin();
  const company = companies.find(c => c.id === companyId);

  const [search, setSearch] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingMemberId, setEditingMemberId] = useState<string | null>(null);
  const [memberPendingDelete, setMemberPendingDelete] =
    useState<MemberListRow | null>(null);
  const formAddMember = useMemo(() => createForm({}), []);
  const formEditMember = useMemo(() => createForm({}), []);

  const employeeNameCandidates = useMemo(
    () => (company ? employeeListCompanyNameCandidates(company) : []),
    [company]
  );

  const memberFilterParams = useMemo(
    () =>
      companyId
        ? {
            [SERVICE_DESK_ADD_MEMBER_COMPANY_FK_FIELD]: companyId,
            company_id: companyId,
          }
        : undefined,
    [companyId]
  );

  const {
    data: employeeListPayload,
    isPending: employeeListLoading,
    isSuccess: employeeListSuccess,
    isError: employeeListError,
  } = useQuery({
    queryKey: serviceDeskEmployeeListByCompanyQueryKey(companyId ?? ""),
    queryFn: async () => {
      if (!company || !companyId) {
        throw new Error("No company");
      }
      const candidates = employeeListCompanyNameCandidates(company);

      const listEmployees = async (company_name?: string) => {
        const params: Record<string, string> = {
          company_id: companyId,
        };
        if (company_name) {
          params.company_name = company_name;
        }
        return ServiceDeskAPI.employees.list(params);
      };

      let last: unknown = null;

      if (candidates.length === 0) {
        const res = await listEmployees();
        return res.data;
      }

      for (const company_name of candidates) {
        const res = await listEmployees(company_name);
        last = res.data;
        if (extractEmployeeListRows(last).length > 0) {
          return last;
        }
      }
      return last;
    },
    enabled: Boolean(company && companyId),
    staleTime: 15_000,
    retry: 1,
  });

  const employeesFromList = useMemo(() => {
    const rows = extractEmployeeListRows(employeeListPayload ?? null).filter(
      (r): r is Record<string, unknown> =>
        r !== null && typeof r === "object" && !Array.isArray(r)
    );
    return filterMemberRawRowsByCompany(rows, companyId, "lenient").map(r =>
      mapEmployeeRowToMember(r)
    );
  }, [employeeListPayload, companyId]);

  const primaryFromEmployeeList =
    employeeListSuccess && employeesFromList.length > 0;

  useEffect(() => {
    if (primaryFromEmployeeList && addOpen) {
      setAddOpen(false);
      formAddMember.reset();
    }
  }, [primaryFromEmployeeList, addOpen, formAddMember]);

  const {
    data: membersGridPayload,
    isPending: membersGridLoading,
    isError: membersError,
  } = useQuery({
    queryKey: serviceDeskFormilyAddMemberGridQueryKey(companyId ?? ""),
    queryFn: async () => {
      const data = await ServiceDeskAPIService.formily.getGridData(
        SERVICE_DESK_FORMILY_ADD_MEMBER_MODEL,
        1,
        200,
        memberFilterParams
      );
      if (data === undefined) {
        throw new Error("Failed to load members");
      }
      return data;
    },
    enabled: Boolean(
      companyId &&
        company &&
        !employeeListLoading &&
        !primaryFromEmployeeList
    ),
    staleTime: 15_000,
    retry: 1,
  });

  const membersFromFormilyGrid = useMemo(() => {
    const rows = extractListArray(membersGridPayload ?? null).filter(
      (r): r is Record<string, unknown> =>
        r !== null && typeof r === "object" && !Array.isArray(r)
    );
    return filterMemberRawRowsByCompany(rows, companyId, "strict").map(r =>
      mapFormilyRowToMember(r)
    );
  }, [membersGridPayload, companyId]);

  const membersFromApi = useMemo((): MemberListRow[] => {
    if (employeeListSuccess && employeesFromList.length > 0) {
      return employeesFromList;
    }
    return membersFromFormilyGrid;
  }, [employeeListSuccess, employeesFromList, membersFromFormilyGrid]);

  const membersLoading =
    employeeListLoading ||
    (!primaryFromEmployeeList && membersGridLoading);

  const filteredMembers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return membersFromApi;
    return membersFromApi.filter(
      m =>
        m.name.toLowerCase().includes(q) ||
        m.email.toLowerCase().includes(q) ||
        m.role.toLowerCase().includes(q) ||
        (m.employeeMetaLine?.toLowerCase().includes(q) ?? false)
    );
  }, [membersFromApi, search]);

  const { data: memberSchemaInfo, isLoading: memberSchemaLoading } = useQuery({
    queryKey: ["schema", SERVICE_DESK_FORMILY_ADD_MEMBER_MODEL],
    queryFn: () =>
      ServiceDeskAPIService.schema.getByName(SERVICE_DESK_FORMILY_ADD_MEMBER_MODEL),
    enabled: addOpen || editOpen,
    staleTime: 5 * 60_000,
  });

  const {
    data: editMemberFormilyDetail,
    isPending: editMemberDetailLoading,
    isError: editMemberDetailError,
    error: editMemberDetailErr,
  } = useQuery({
    queryKey: [
      "sd-admin-member-formily-edit",
      SERVICE_DESK_FORMILY_ADD_MEMBER_MODEL,
      editingMemberId,
    ],
    queryFn: async () => {
      const data = await ServiceDeskAPIService.formily.getItemById(
        SERVICE_DESK_FORMILY_ADD_MEMBER_MODEL,
        editingMemberId!
      );
      if (data === undefined || data === null) {
        throw new Error("Member record not found");
      }
      return data as Record<string, unknown>;
    },
    enabled: Boolean(editOpen && editingMemberId),
    staleTime: 15_000,
    retry: 1,
  });

  useEffect(() => {
    if (!addOpen) {
      return;
    }
    formAddMember.reset();
  }, [addOpen, formAddMember]);

  useEffect(() => {
    if (!addOpen || !companyId) {
      return;
    }
    formAddMember.setInitialValues({
      [SERVICE_DESK_ADD_MEMBER_COMPANY_FK_FIELD]: companyId,
      company_id: companyId,
    });
  }, [addOpen, companyId, formAddMember]);

  useEffect(() => {
    if (!editOpen || !editingMemberId) {
      return;
    }
    formEditMember.reset();
  }, [editOpen, editingMemberId, formEditMember]);

  useEffect(() => {
    if (!editMemberFormilyDetail || !editingMemberId) {
      return;
    }
    formEditMember.setInitialValues(
      removeMetaFields(editMemberFormilyDetail)
    );
  }, [editMemberFormilyDetail, editingMemberId, formEditMember]);

  const saveAddMember = async () => {
    if (!companyId) {
      return;
    }
    try {
      await formAddMember.validate();
      let formValues = removeMetaFields(formAddMember.values) as Record<
        string,
        unknown
      >;
      formValues = normalizeDynamicModuleUploadFields(
        memberSchemaInfo?.json ?? memberSchemaInfo,
        formValues
      );
      formValues[SERVICE_DESK_ADD_MEMBER_COMPANY_FK_FIELD] = companyId;
      formValues.company_id = companyId;
      const response = await ServiceDeskAPIService.formily.createItem(
        SERVICE_DESK_FORMILY_ADD_MEMBER_MODEL,
        formValues
      );
      const resObj = response as Record<string, unknown> | null | undefined;
      const newId = resObj?.id ?? resObj?.pk;
      if (!newId) {
        notify.error("Failed to get member ID after creation");
        return;
      }
      toast.success("Member added");
      setAddOpen(false);
      formAddMember.reset();
      void queryClient.invalidateQueries({
        queryKey: ["service-desk", "employee_list"],
      });
      void queryClient.invalidateQueries({
        queryKey: serviceDeskFormilyAddMemberGridQueryKey(companyId),
      });
    } catch (error) {
      toast.error("Please fill in all required fields correctly");
    }
  };

  const saveEditMember = async () => {
    if (!editingMemberId || !companyId) {
      return;
    }
    try {
      await formEditMember.validate();
      let formValues = removeMetaFields(formEditMember.values) as Record<
        string,
        unknown
      >;
      if (editMemberFormilyDetail) {
        formValues = {
          ...removeMetaFields(
            editMemberFormilyDetail as Record<string, unknown>
          ),
          ...formValues,
        };
      }
      formValues = normalizeDynamicModuleUploadFields(
        memberSchemaInfo?.json ?? memberSchemaInfo,
        formValues
      );
      formValues[SERVICE_DESK_ADD_MEMBER_COMPANY_FK_FIELD] = companyId;
      formValues.company_id = companyId;
      await ServiceDeskAPIService.formily.updateItem(
        SERVICE_DESK_FORMILY_ADD_MEMBER_MODEL,
        editingMemberId,
        formValues
      );
      const memberId = editingMemberId;
      toast.success("Member updated");
      setEditOpen(false);
      setEditingMemberId(null);
      formEditMember.reset();
      void queryClient.invalidateQueries({
        queryKey: ["service-desk", "employee_list"],
      });
      void queryClient.invalidateQueries({
        queryKey: serviceDeskFormilyAddMemberGridQueryKey(companyId),
      });
      void queryClient.invalidateQueries({
        queryKey: [
          "sd-admin-member-formily-edit",
          SERVICE_DESK_FORMILY_ADD_MEMBER_MODEL,
          memberId,
        ],
      });
    } catch (error) {
      toast.error("Please fix validation errors or try again");
    }
  };

  const deleteMember = async (memberId: string): Promise<boolean> => {
    try {
      await ServiceDeskAPIService.formily.deleteItem(
        SERVICE_DESK_FORMILY_ADD_MEMBER_MODEL,
        memberId
      );
      toast.success("Member removed");
      void queryClient.invalidateQueries({
        queryKey: ["service-desk", "employee_list"],
      });
      if (companyId) {
        void queryClient.invalidateQueries({
          queryKey: serviceDeskFormilyAddMemberGridQueryKey(companyId),
        });
      }
      return true;
    } catch {
      toast.error("Could not delete member on the server");
      return false;
    }
  };

  const downloadTemplate = () => {
    const csv = `${CSV_HEADER}\nJane Doe,jane.doe@example.com,+1 555-0100,IT Manager`;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "members-import-template.csv";
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Template downloaded");
  };

  const onImportFile = (file: File | null) => {
    if (!file) return;
    toast.info(
      "CSV import is not wired to the Formily API. Use Add Member or import on the server."
    );
  };

  if (!company) {
    return (
      <div className="space-y-4 p-6">
        <p className="text-muted-foreground">Company not found.</p>
        <Button variant="outline" asChild>
          <Link to={sd("admin/companies")}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to companies
          </Link>
        </Button>
      </div>
    );
  }

  const domainLabel = company.domain.startsWith("@")
    ? company.domain
    : `@${company.domain}`;

  return (
    <div className="min-w-0 w-full max-w-none space-y-6 p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2 text-muted-foreground"
            asChild
          >
            <Link to={sd("admin/companies")}>
              <ArrowLeft className="mr-1 h-4 w-4" />
              Companies
            </Link>
          </Button>
          <div className="flex items-start gap-3">
            <div
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg text-lg font-bold text-primary-foreground"
              style={{ backgroundColor: `hsl(${company.logoColor})` }}
            >
              {company.name.charAt(0)}
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">
                {company.name} — Members
              </h1>
              <p className="mt-0.5 max-w-3xl text-sm text-muted-foreground">
                Team members associated with {domainLabel}. They can submit and
                follow tickets.
              </p>
            </div>
          </div>
        </div>
        <Badge
          variant="secondary"
          className="shrink-0 gap-1.5 px-3 py-1.5 text-sm"
        >
          <Users className="h-4 w-4" />
          {membersFromApi.length}
        </Badge>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative min-w-0 flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search members..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        {!primaryFromEmployeeList ? (
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={downloadTemplate}
            >
              <Download className="mr-2 h-4 w-4" />
              Template
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => importInputRef.current?.click()}
            >
              <Upload className="mr-2 h-4 w-4" />
              Import
            </Button>
            <input
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              ref={importInputRef}
              onChange={e => {
                const f = e.target.files?.[0];
                if (f) onImportFile(f);
                e.target.value = "";
              }}
            />
            <Button
              type="button"
              size="sm"
              onClick={() => {
                formAddMember.reset();
                setAddOpen(true);
              }}
            >
              <UserPlus className="mr-2 h-4 w-4" />
              Add Member
            </Button>
          </div>
        ) : null}
      </div>

      {employeeListError ? (
        <p className="rounded-md border border-amber-500/35 bg-amber-500/10 px-3 py-2 text-sm text-amber-950 dark:text-amber-100">
          Employee list (
          <code className="text-xs">/service_desk/employee_list/?company_name=…</code>)
          failed (tried company_name:{" "}
          <span className="font-medium">
            {employeeNameCandidates.length > 0
              ? employeeNameCandidates.join(", ")
              : "—"}
          </span>
          ). Showing Formily members if available.
        </p>
      ) : null}

      {!primaryFromEmployeeList && membersError ? (
        <p className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          Could not load members from Formily. Confirm &quot;
          {SERVICE_DESK_FORMILY_ADD_MEMBER_MODEL}&quot; and the filter field
          &quot;{SERVICE_DESK_ADD_MEMBER_COMPANY_FK_FIELD}&quot; match your
          backend.
        </p>
      ) : null}

      <div className="rounded-xl border border-border bg-card">
        <ul className="divide-y divide-border">
          {membersLoading ? (
            <li className="flex justify-center py-12">
              <LoadingSpinner />
            </li>
          ) : filteredMembers.length === 0 ? (
            <li className="p-8 text-center text-sm text-muted-foreground">
              No members match your search.
            </li>
          ) : (
            filteredMembers.map(m => (
              <li
                key={`${m.listRowSource}-${m.id}`}
                className="flex flex-col gap-3 p-4 transition-colors hover:bg-muted/40 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="flex min-w-0 w-full flex-1 flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-8">
                  <div className="flex min-w-0 items-center gap-3">
                    <div
                      className={cn(
                        "flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold",
                        "bg-primary/15 text-primary"
                      )}
                    >
                      {m.name.charAt(0)}
                    </div>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold">{m.name}</span>
                        {m.isPrimary ? (
                          <Badge className="h-5 bg-primary px-1.5 text-[10px]">
                            Primary
                          </Badge>
                        ) : null}
                      </div>
                      <div className="mt-0.5 flex items-center gap-1.5 text-sm text-muted-foreground">
                        <Briefcase className="h-3.5 w-3.5 shrink-0" />
                        {m.listRowSource === "formily" ? (
                          <span className="min-w-0 truncate">
                            <span className="text-muted-foreground">
                              Job title ·{" "}
                            </span>
                            {m.role}
                          </span>
                        ) : (
                          <span className="truncate">{m.role}</span>
                        )}
                      </div>
                      {m.employeeMetaLine ? (
                        <p className="mt-0.5 line-clamp-2 text-xs leading-snug text-muted-foreground/90">
                          {m.employeeMetaLine}
                        </p>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex min-w-0 flex-1 flex-col gap-3 pl-[52px] text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-end sm:gap-x-16 sm:pl-0 lg:gap-x-24">
                    <div className="flex min-w-0 flex-1 items-center gap-1.5 sm:max-w-none">
                      <Mail className="h-3.5 w-3.5 shrink-0" />
                      <span className="truncate">{m.email}</span>
                    </div>
                    <div className="flex shrink-0 items-center gap-1.5 whitespace-nowrap">
                      <Phone className="h-3.5 w-3.5" />
                      <span>{m.phone}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 pl-[52px] sm:pl-0">
                  {m.listRowSource === "formily" ? (
                    <Button
                      variant="ghost"
                      size="icon"
                      type="button"
                      className="text-muted-foreground"
                      title="Edit member"
                      aria-label={`Edit ${m.name}`}
                      onClick={() => {
                        formEditMember.reset();
                        setEditingMemberId(m.id);
                        setEditOpen(true);
                      }}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  ) : null}
                  {m.listRowSource === "formily" ? (
                    <Button
                      variant="ghost"
                      size="icon"
                      type="button"
                      className="text-destructive hover:text-destructive"
                      aria-label={`Remove ${m.name}`}
                      onClick={() => setMemberPendingDelete(m)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  ) : null}
                </div>
              </li>
            ))
          )}
        </ul>
      </div>

      <SchemaFormDialog
        variant="premium"
        open={addOpen}
        onOpenChange={o => {
          setAddOpen(o);
          if (!o) formAddMember.reset();
        }}
        title={`Add member — ${company.name}`}
        description={`${SERVICE_DESK_FORMILY_ADD_MEMBER_MODEL} — fields match your Formily schema.`}
        form={formAddMember}
        schemaInfo={memberSchemaInfo}
        isLoading={memberSchemaLoading}
        onSave={() => void saveAddMember()}
        onCancel={() => setAddOpen(false)}
        saveLabel="Add member"
        saveIcon={<UserPlus className="h-4 w-4" />}
      />

      <SchemaFormDialog
        variant="premium"
        open={editOpen}
        onOpenChange={o => {
          setEditOpen(o);
          if (!o) {
            setEditingMemberId(null);
            formEditMember.reset();
          }
        }}
        title={`Edit member — ${company.name}`}
        description={
          editingMemberId ? (
            <>
              <span className="font-mono text-xs">{editingMemberId}</span>
              <span className="mx-1">·</span>
              {SERVICE_DESK_FORMILY_ADD_MEMBER_MODEL}
            </>
          ) : undefined
        }
        form={formEditMember}
        schemaInfo={memberSchemaInfo}
        isLoading={memberSchemaLoading}
        isDetailLoading={editMemberDetailLoading}
        isDetailError={editMemberDetailError || !editMemberFormilyDetail}
        errorMessage={
          <>
            {editMemberDetailErr instanceof Error
              ? editMemberDetailErr.message
              : "Could not load this member for editing."}{" "}
            <span className="text-muted-foreground">
              Row ID must match the Formily record ID.
            </span>
          </>
        }
        onSave={() => void saveEditMember()}
        onCancel={() => {
          setEditOpen(false);
          setEditingMemberId(null);
          formEditMember.reset();
        }}
        saveLabel="Save changes"
      />

      <Dialog
        open={memberPendingDelete !== null}
        onOpenChange={open => {
          if (!open) {
            setMemberPendingDelete(null);
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Remove member?</DialogTitle>
            <DialogDescription>
              {memberPendingDelete ? (
                <>
                  This will delete{" "}
                  <span className="font-medium text-foreground">
                    {memberPendingDelete.name}
                  </span>{" "}
                  ({memberPendingDelete.email}) from the server.
                </>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              type="button"
              onClick={() => setMemberPendingDelete(null)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              type="button"
              onClick={async () => {
                const m = memberPendingDelete;
                if (!m) return;
                const ok = await deleteMember(m.id);
                if (ok) {
                  setMemberPendingDelete(null);
                }
              }}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
